/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE } from "utils";

function UsersKhususApelSingleDate() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tanggal, setTanggal] = useState("");

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State untuk modal detail
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  // Set default date on component mount
  useEffect(() => {
    const today = new Date();
    setTanggal(today.toISOString().split("T")[0]);

    // Auto-load data with default date
    fetchData(today.toISOString().split("T")[0]);
  }, []);

  const fetchData = async (tanggal) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}apel-harian/users-khusus/tanggal/${tanggal}`
      );

      if (response.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data apel harian users khusus",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGetData = () => {
    if (!tanggal) {
      swal({
        title: "Error",
        text: "Tanggal harus diisi",
        icon: "error",
      });
      return;
    }

    fetchData(tanggal);
  };

  // Fungsi untuk membuka modal detail
  const handleOpenDetailModal = (pegawai) => {
    setSelectedDetail(pegawai);
    setOpenDetailModal(true);
  };

  // Fungsi untuk menutup modal detail
  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectedDetail(null);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleExportExcel = () => {
    if (!data || !data.data_pegawai) {
      swal({
        title: "Error",
        text: "Tidak ada data untuk diekspor",
        icon: "error",
      });
      return;
    }

    try {
      // Create HTML table
      let tableHTML = `
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>Nama Pegawai</th>
              <th>Jabatan</th>
              <th>Dinas</th>
              <th>Bidang</th>
              <th>Pangkat</th>
              <th>Status Apel Pagi</th>
              <th>Status Apel Siang</th>
              <th>Status Apel</th>
            </tr>
          </thead>
          <tbody>
      `;

      data.data_pegawai.forEach((pegawai, index) => {
        tableHTML += `
          <tr>
            <td>${index + 1}</td>
            <td>${pegawai.nama_pegawai}</td>
            <td>${pegawai.nama_jabatan}</td>
            <td>${pegawai.nama_dinas}</td>
            <td>${pegawai.nama_bidang}</td>
            <td>${pegawai.pangkat}</td>
            <td>${pegawai.apel_pagi.status || "Tidak Ada"}</td>
            <td>${pegawai.apel_siang.status || "Tidak Ada"}</td>
            <td>${pegawai.status_apel}</td>
          </tr>
        `;
      });

      tableHTML += `
          </tbody>
        </table>
      `;

      // Convert to Excel
      const blob = new Blob([tableHTML], { type: "application/vnd.ms-excel" });
      const fileName = `Apel_Harian_Users_Khusus_${tanggal}.xls`;
      saveAs(blob, fileName);

      swal({
        title: "Success",
        text: "Data berhasil diekspor ke Excel",
        icon: "success",
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      swal({
        title: "Error",
        text: "Gagal mengekspor data",
        icon: "error",
      });
    }
  };

  const columns = [
    { Header: "No", accessor: "no", width: "5%" },
    {
      Header: "Profil",
      accessor: "profil",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
            alt={row.original.nama_pegawai}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              // Jika gambar tidak ditemukan, tampilkan icon default
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 20 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    { Header: "Nama Pegawai", accessor: "nama_pegawai", width: "20%" },
    { Header: "Jabatan", accessor: "nama_jabatan", width: "15%" },
    {
      Header: "Dinas",
      accessor: "nama_dinas",
      width: 200,
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }) => (
        <MDTypography
          variant="caption"
          color="text"
          fontWeight="medium"
          sx={{
            wordWrap: "break-word",
            whiteSpace: "normal",
            lineHeight: 1.4,
            width: "200px",
            maxWidth: "200px",
            minWidth: "200px",
            display: "block",
          }}
        >
          {value}
        </MDTypography>
      ),
    },
    { Header: "Pangkat", accessor: "pangkat", width: "8%" },
    { Header: "Status Apel Pagi", accessor: "status_apel_pagi", width: "12%" },
    {
      Header: "Status Apel Siang",
      accessor: "status_apel_siang",
      width: "12%",
    },
    { Header: "Status Apel", accessor: "status_apel", width: "11%" },
  ];

  const rows =
    data?.data_pegawai?.map((pegawai, index) => ({
      no: index + 1,
      nama_pegawai: pegawai.nama_pegawai,
      nama_jabatan: pegawai.nama_jabatan,
      uid_RSS: pegawai.uid_RSS,
      nama_dinas: pegawai.nama_dinas,
      nama_bidang: pegawai.nama_bidang,
      pangkat: pegawai.pangkat,
      status_apel_pagi: (
        <Chip
          label={pegawai.apel_pagi.status || "Tidak Ada"}
          color={pegawai.apel_pagi.status === "Hadir" ? "success" : "warning"}
          size="small"
        />
      ),
      status_apel_siang: (
        <Chip
          label={pegawai.apel_siang.status || "Tidak Ada"}
          color={pegawai.apel_siang.status === "Hadir" ? "success" : "warning"}
          size="small"
        />
      ),
      status_apel: (
        <Chip
          label={pegawai.status_apel}
          color={
            pegawai.status_apel === "Lengkap"
              ? "success"
              : pegawai.status_apel === "Sebagian"
              ? "warning"
              : "error"
          }
          size="small"
        />
      ),
    })) || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          {/* Statistik Card dengan Filter Tanggal */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                >
                  <MDBox>
                    <MDTypography variant="h6" gutterBottom>
                      Statistik Apel Harian Pimpinan
                    </MDTypography>
                    <MDBox display="flex" gap={1} mt={1}>
                      <MDButton
                        variant="outlined"
                        color="success"
                        size="small"
                        onClick={handleGetData}
                        disabled={loading}
                      >
                        <Icon>refresh</Icon>
                        &nbsp;Refresh
                      </MDButton>
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <MDInput
                      type="date"
                      label="Pilih Tanggal"
                      value={tanggal}
                      onChange={(e) => {
                        setTanggal(e.target.value);
                        fetchData(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </MDBox>
                </MDBox>

                {data && (
                  <>
                    <MDTypography variant="h6" color="text" mb={2}>
                      Rekap Apel Harian - Pimpinan Daerah dan Perangkat Daerah -{" "}
                      {new Date(data.tanggal).toLocaleDateString("id-ID")}
                    </MDTypography>

                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <Chip
                          icon={<Icon sx={{ color: "white" }}>group</Icon>}
                          label={`Total: ${data.statistik.total_pegawai}`}
                          variant="filled"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                            backgroundColor: "#424242",
                            color: "white",
                            "& .MuiChip-icon": {
                              color: "white",
                            },
                            "& .MuiChip-label": {
                              color: "white",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>check_circle</Icon>}
                          label={`Lengkap: ${data.statistik.lengkap}`}
                          color="success"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>warning</Icon>}
                          label={`Sebagian: ${data.statistik.sebagian}`}
                          color="warning"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>trending_up</Icon>}
                          label={`Persentase Lengkap: ${data.statistik.persentase_lengkap}%`}
                          color="info"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Tabel Data Pegawai */}
          {data && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text">
                        Data Apel Pegawai:{" "}
                        {new Date(data.tanggal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total: {data.data_pegawai.length} pegawai
                      </MDTypography>
                    </MDBox>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={handleExportExcel}
                      disabled={loading}
                    >
                      <Icon>download</Icon>
                      &nbsp;Export Excel
                    </MDButton>
                  </MDBox>
                  {isMobile ? (
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {loading && (
                        <MDTypography variant="button" color="text.secondary">
                          Memuat...
                        </MDTypography>
                      )}
                      {!loading &&
                        data?.data_pegawai?.map((pegawai, index) => (
                          <Card
                            key={`${pegawai.uid_RSS || "pegawai"}-${index}`}
                          >
                            <CardContent>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={4}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <img
                                      src={`https://smartdh.rotendaokab.go.id/uploads/${pegawai.uid_RSS}.jpg`}
                                      alt={pegawai.nama_pegawai}
                                      style={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        border: "2px solid #e0e0e0",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display =
                                          "flex";
                                      }}
                                    />
                                    <MDBox
                                      display="none"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        backgroundColor: "grey.200",
                                        color: "grey.500",
                                      }}
                                    >
                                      <Icon sx={{ fontSize: 36 }}>person</Icon>
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={8}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                  >
                                    {pegawai.nama_pegawai}
                                  </MDTypography>
                                  <MDBox
                                    mt={1}
                                    display="flex"
                                    flexWrap="wrap"
                                    gap={0.5}
                                  >
                                    <Chip
                                      label={`Apel Pagi: ${
                                        pegawai.apel_pagi.status || "Tidak Ada"
                                      }`}
                                      color={
                                        pegawai.apel_pagi.status === "Hadir"
                                          ? "success"
                                          : "warning"
                                      }
                                      size="small"
                                    />
                                    <Chip
                                      label={`Apel Siang: ${
                                        pegawai.apel_siang.status || "Tidak Ada"
                                      }`}
                                      color={
                                        pegawai.apel_siang.status === "Hadir"
                                          ? "success"
                                          : "warning"
                                      }
                                      size="small"
                                    />
                                    <Chip
                                      label={`Status: ${pegawai.status_apel}`}
                                      color={
                                        pegawai.status_apel === "Lengkap"
                                          ? "success"
                                          : pegawai.status_apel === "Sebagian"
                                          ? "warning"
                                          : "error"
                                      }
                                      size="small"
                                      onClick={() =>
                                        handleOpenDetailModal(pegawai)
                                      }
                                      sx={{ cursor: "pointer" }}
                                    />
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Jabatan: {pegawai.nama_jabatan}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Dinas: {pegawai.nama_dinas}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Bidang: {pegawai.nama_bidang}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Pangkat: {pegawai.pangkat}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}
                    </MDBox>
                  ) : (
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                      loading={loading}
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="body1" color="text">
                    Memuat data apel harian users khusus...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!data && !loading && tanggal && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={4}
                  >
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
                      event_busy
                    </Icon>
                    <MDTypography variant="h6" color="text.secondary">
                      Tidak ada data apel harian users khusus
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Coba pilih tanggal lain atau periksa kembali data
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
      <Footer />

      {/* Modal Detail Kegiatan */}
      <Dialog
        open={openDetailModal}
        onClose={handleCloseDetailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">
              Detail Apel - {selectedDetail && selectedDetail.nama_pegawai}
            </MDTypography>
            <IconButton onClick={handleCloseDetailModal} size="small">
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {selectedDetail && (
            <MDBox>
              <MDTypography variant="body2" color="text.secondary" mb={2}>
                <MDBox mb={2}>
                  <MDTypography variant="subtitle2" fontWeight="bold">
                    Informasi Pegawai:
                  </MDTypography>
                  <MDTypography variant="body2">
                    Nama: {selectedDetail.nama_pegawai}
                  </MDTypography>
                  <MDTypography variant="body2">
                    Jabatan: {selectedDetail.nama_jabatan}
                  </MDTypography>
                  <MDTypography variant="body2">
                    Dinas: {selectedDetail.nama_dinas}
                  </MDTypography>
                  <MDTypography variant="body2">
                    Bidang: {selectedDetail.nama_bidang}
                  </MDTypography>
                  <MDTypography variant="body2">
                    Pangkat: {selectedDetail.pangkat}
                  </MDTypography>
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography variant="subtitle2" fontWeight="bold">
                    Status Apel Pagi:
                  </MDTypography>
                  <Chip
                    label={selectedDetail.apel_pagi.status || "Tidak Ada"}
                    color={
                      selectedDetail.apel_pagi.status === "Hadir"
                        ? "success"
                        : "warning"
                    }
                    size="small"
                  />
                  {selectedDetail.apel_pagi.jam_absen && (
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={1}
                    >
                      Jam Absen: {selectedDetail.apel_pagi.jam_absen}
                    </MDTypography>
                  )}
                  {selectedDetail.apel_pagi.keterangan && (
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={1}
                    >
                      Keterangan: {selectedDetail.apel_pagi.keterangan}
                    </MDTypography>
                  )}
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography variant="subtitle2" fontWeight="bold">
                    Status Apel Siang:
                  </MDTypography>
                  <Chip
                    label={selectedDetail.apel_siang.status || "Tidak Ada"}
                    color={
                      selectedDetail.apel_siang.status === "Hadir"
                        ? "success"
                        : "warning"
                    }
                    size="small"
                  />
                  {selectedDetail.apel_siang.jam_absen && (
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={1}
                    >
                      Jam Absen: {selectedDetail.apel_siang.jam_absen}
                    </MDTypography>
                  )}
                  {selectedDetail.apel_siang.keterangan && (
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={1}
                    >
                      Keterangan: {selectedDetail.apel_siang.keterangan}
                    </MDTypography>
                  )}
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography variant="subtitle2" fontWeight="bold">
                    Status Apel Keseluruhan:
                  </MDTypography>
                  <Chip
                    label={selectedDetail.status_apel}
                    color={
                      selectedDetail.status_apel === "Lengkap"
                        ? "success"
                        : selectedDetail.status_apel === "Sebagian"
                        ? "warning"
                        : "error"
                    }
                    size="small"
                  />
                </MDBox>
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDetailModal} color="secondary">
            Tutup
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default UsersKhususApelSingleDate;
